import { useState } from "react";
import { Link } from "react-router-dom";
import { Download } from "lucide-react";
import { fetchStatementPdf, useStatement, type StatementLine } from "@/api/statements";
import { RequestError } from "@/components/RequestError";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { TextField } from "@/components/ui/field";
import { saveBlob } from "@/lib/files";
import { formatDate, formatMoney } from "@/lib/format";
import { cn } from "@/lib/utils";

const KIND_LABEL: Record<StatementLine["kind"], string> = {
  invoice: "Invoice",
  payment: "Payment received",
  write_off: "Written off",
  credit_note: "Credit note",
};

/** The running account for one client: what was raised, what came back, what is still owed. */
export function StatementPanel({ clientId }: { clientId: string }) {
  const [since, setSince] = useState("");
  const [asOf, setAsOf] = useState("");
  const [busy, setBusy] = useState(false);
  const [fileError, setFileError] = useState<unknown>(null);

  const range = { from: since || undefined, as_of: asOf || undefined };
  const statement = useStatement(clientId, range);

  const download = async () => {
    setBusy(true);
    setFileError(null);
    try {
      const file = await fetchStatementPdf(clientId, range);
      await saveBlob(file.blob, file.filename);
    } catch (error) {
      setFileError(error);
    } finally {
      setBusy(false);
    }
  };

  if (statement.isPending) {
    return <p className="text-muted-foreground">Working out the account…</p>;
  }
  if (statement.isError) {
    return <RequestError error={statement.error} onReload={() => void statement.refetch()} />;
  }

  const data = statement.data;
  const owed = Number(data.closing_balance) > 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <TextField
          id="statement-from"
          label="From"
          type="date"
          value={since}
          onChange={setSince}
          hint="Leave blank to start at the beginning"
        />
        <TextField
          id="statement-as-of"
          label="As at"
          type="date"
          value={asOf}
          onChange={setAsOf}
          hint="Leave blank for today"
        />
        <Button variant="outline" size="sm" disabled={busy} onClick={() => void download()}>
          <Download className="h-3.5 w-3.5" aria-hidden />
          {busy ? "Preparing…" : "Download PDF"}
        </Button>
      </div>
      <RequestError error={fileError} />

      <div className="grid gap-3 sm:grid-cols-4">
        <Figure label="Brought forward" value={formatMoney(data.opening_balance, data.currency)} />
        <Figure label="Invoiced" value={formatMoney(data.invoiced, data.currency)} />
        <Figure
          label="Received"
          value={formatMoney(data.received, data.currency)}
          tone="text-success"
        />
        <Figure
          label="Balance due"
          value={formatMoney(data.closing_balance, data.currency)}
          tone={owed ? "text-destructive" : "text-success"}
        />
      </div>

      {data.awaiting_bank_letter && (
        <p className="rounded-md border border-warning/40 bg-warning/10 p-3 text-sm">
          Some of this cannot fall due yet: the Director-signed bank details letter has not been
          recorded as sent, and payment runs from the later of the invoice due date and seven
          days after that letter (SLA 6.4).
        </p>
      )}

      {Number(data.late_charge_accrued) > 0 && (
        <p className="rounded-md border p-3 text-sm">
          Late charge accrued under SLA 6.5, not yet billed:{" "}
          <strong>{formatMoney(data.late_charge_accrued, data.currency)}</strong>. It is not on the
          PDF; bill it from the invoice page if the directors decide to.
        </p>
      )}

      {data.lines.length === 0 ? (
        <EmptyState
          title="Nothing on the account"
          description="No issued invoices or payments fall in this period."
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="data-table">
              <caption className="sr-only">Statement of account</caption>
              <thead className="border-b bg-muted/40 text-xs text-muted-foreground">
                <tr>
                  <th scope="col" className="p-2 text-left font-medium">
                    Date
                  </th>
                  <th scope="col" className="p-2 text-left font-medium">
                    Reference
                  </th>
                  <th scope="col" className="hidden p-2 text-left font-medium sm:table-cell">
                    Particulars
                  </th>
                  <th scope="col" className="p-2 text-right font-medium">
                    Debit
                  </th>
                  <th scope="col" className="p-2 text-right font-medium">
                    Credit
                  </th>
                  <th scope="col" className="p-2 text-right font-medium">
                    Balance
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.lines.map((line, index) => (
                  <tr key={`${line.reference}-${line.kind}-${index}`} className="border-b">
                    <td className="p-2 whitespace-nowrap">{formatDate(line.on)}</td>
                    <td className="p-2">
                      {line.invoice_id ? (
                        <Link to={`/invoices/${line.invoice_id}`} className="hover:underline">
                          {line.reference}
                        </Link>
                      ) : (
                        line.reference
                      )}
                    </td>
                    <td className="hidden p-2 text-muted-foreground sm:table-cell">
                      {KIND_LABEL[line.kind]}
                      {line.description ? ` · ${line.description}` : ""}
                    </td>
                    <td className="p-2 text-right tabular-nums">
                      {Number(line.debit) ? formatMoney(line.debit, data.currency) : ""}
                    </td>
                    <td className="p-2 text-right tabular-nums">
                      {Number(line.credit) ? formatMoney(line.credit, data.currency) : ""}
                    </td>
                    <td className="p-2 text-right font-medium tabular-nums">
                      {formatMoney(line.balance, data.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {data.ageing.length > 0 && (
        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium text-primary">How old the outstanding amount is</p>
          <ul className="flex flex-wrap gap-2">
            {data.ageing.map((slice) => (
              <li key={slice.key}>
                <Badge variant={slice.key === "not_due" ? "secondary" : "destructive"}>
                  {slice.label}: {formatMoney(slice.amount, data.currency)}
                </Badge>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Figure({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="rounded-lg border bg-card p-3 shadow-sm">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={cn("text-lg font-semibold tabular-nums", tone ?? "text-primary")}>{value}</p>
    </div>
  );
}
